import { useState, useEffect } from "react";
import {
  IoNotificationsOutline,
  IoCheckmarkCircleOutline,
} from "react-icons/io5";
import { Button } from "../ui/button";
import {
  hasAskedNotificationPermission,
  saveNotificationPermission,
} from "@/utils/notificationPermission";
import { useUpdateUserPreferences } from "@/hooks/useUserPreferences";
import toast from "react-hot-toast";

type NotificationPermissionModalProps = {
  onPermissionGranted: () => void;
  onPermissionDenied: () => void;
};

export const NotificationPermissionModal = ({
  onPermissionGranted,
  onPermissionDenied,
}: NotificationPermissionModalProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const updatePreferences = useUpdateUserPreferences();

  useEffect(() => {
    // Verificar se já perguntou antes
    const hasAsked = hasAskedNotificationPermission();

    if (!hasAsked) {
      // Aguardar um pouco antes de mostrar o modal (melhor UX)
      const timer = setTimeout(() => {
        setIsOpen(true);
      }, 2000); // 2 segundos após carregar a página

      return () => clearTimeout(timer);
    }
  }, []);

  const handleAllow = async () => {
    try {
      // Atualizar preferências na API (ativar todos os toggles)
      await updatePreferences.mutateAsync({
        notifyStatusUpdates: true,
        notifyCityNews: true,
        notifyGovernmentReplies: true,
        notifyNeighborhoodNews: true,
      });

      // Salvar no localStorage
      saveNotificationPermission(true);
      setIsOpen(false);
      onPermissionGranted();
    } catch (error) {
      console.error("Erro ao ativar notificações:", error);
      toast.error("Erro ao salvar preferências. Tente novamente.");
    }
  };

  const handleDeny = async () => {
    try {
      // Atualizar preferências na API (desativar todos os toggles)
      await updatePreferences.mutateAsync({
        notifyStatusUpdates: false,
        notifyCityNews: false,
        notifyGovernmentReplies: false,
        notifyNeighborhoodNews: false,
      });

      // Salvar no localStorage
      saveNotificationPermission(false);
      setIsOpen(false);
      onPermissionDenied();
    } catch (error) {
      console.error("Erro ao desativar notificações:", error);
      toast.error("Erro ao salvar preferências. Tente novamente.");
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden animate-scale-in">
        {/* Header */}
        <div className="bg-gradient-to-r from-[hsl(var(--primary))] to-[hsl(var(--secondary))] p-6 text-white">
          <div className="flex items-center justify-center mb-4">
            <div className="w-20 h-20 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
              <IoNotificationsOutline className="text-5xl" />
            </div>
          </div>
          <h2 className="text-2xl font-bold text-center">
            Ativar Notificações
          </h2>
        </div>

        {/* Content */}
        <div className="p-6">
          <p className="text-gray-600 text-center mb-6">
            Fique atualizado sobre suas ocorrências em tempo real!
          </p>

          <div className="space-y-4 mb-6">
            <div className="flex items-start gap-3">
              <IoCheckmarkCircleOutline className="text-2xl text-green-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-gray-800">
                  Acompanhe em tempo real
                </h3>
                <p className="text-sm text-gray-600">
                  Receba atualizações instantâneas sobre o status das suas
                  ocorrências
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <IoCheckmarkCircleOutline className="text-2xl text-green-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-gray-800">
                  Respostas da prefeitura
                </h3>
                <p className="text-sm text-gray-600">
                  Seja notificado quando a prefeitura responder suas mensagens
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <IoCheckmarkCircleOutline className="text-2xl text-green-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-gray-800">
                  Nunca perca nada
                </h3>
                <p className="text-sm text-gray-600">
                  Fique por dentro de todas as atualizações importantes
                </p>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="space-y-3">
            <Button
              onClick={handleAllow}
              isLoading={updatePreferences.isPending}
              className="w-full bg-gradient-to-r from-[hsl(var(--primary))] to-[hsl(var(--secondary))] hover:from-[hsl(var(--primary))]/90 hover:to-[hsl(var(--secondary))]/90 shadow-lg"
            >
              Permitir Notificações
            </Button>
            <button
              onClick={handleDeny}
              disabled={updatePreferences.isPending}
              className="w-full py-3 text-gray-600 hover:text-gray-800 font-medium transition-colors disabled:opacity-50"
            >
              Agora não
            </button>
          </div>

          <p className="text-xs text-gray-500 text-center mt-4">
            Você pode alterar essa configuração a qualquer momento nas
            configurações do aplicativo
          </p>
        </div>
      </div>
    </div>
  );
};
